import React, { useState, useEffect, useMemo } from 'react';
import { formatDuration, formatCost, getCallPerformanceScore, getPerformanceScoreColor } from '../../../../../services/callService';
import { getCategoryInfo } from '../../../../../services/memoryService';

const PerformanceTab = ({ userId, callStats, memoryStats, userRole, onRefresh }) => {
  const [timeRange, setTimeRange] = useState('30'); // days
  const [selectedMetric, setSelectedMetric] = useState('overview');

  // Calculate performance insights
  const performanceInsights = useMemo(() => {
    if (!callStats || !memoryStats) return null;

    const insights = [];

    // Call Performance Analysis
    if (callStats.total_calls > 0) {
      // Success Rate Insights
      if (callStats.success_rate >= 80) {
        insights.push({
          type: 'success',
          category: 'calls',
          title: 'Excellent Contact Success Rate',
          description: `${callStats.success_rate}% success rate is above average`,
          recommendation: 'Continue current practices for optimal results',
          icon: '🎯'
        });
      } else if (callStats.success_rate < 50) {
        insights.push({
          type: 'warning',
          category: 'calls',
          title: 'Low Contact Success Rate',
          description: `${callStats.success_rate}% success rate needs improvement`,
          recommendation: 'Review call strategies and consider additional training',
          icon: '⚠️'
        });
      }

      // Duration Analysis
      const avgDuration = callStats.avg_duration_minutes;
      if (avgDuration < 3) {
        insights.push({
          type: 'info',
          category: 'calls',
          title: 'Short Contact Duration',
          description: `Average ${formatDuration(avgDuration)} calls are quite brief`,
          recommendation: 'Consider strategies to extend meaningful conversation time',
          icon: '⏱️'
        });
      } else if (avgDuration > 20) {
        insights.push({
          type: 'info',
          category: 'calls',
          title: 'Long Contact Duration',
          description: `Average ${formatDuration(avgDuration)} calls are extensive`,
          recommendation: 'Monitor for efficiency while maintaining quality',
          icon: '⏰'
        });
      }

      // Latency Performance
      if (callStats.avg_model_latency > 2000) {
        insights.push({
          type: 'warning',
          category: 'performance',
          title: 'High Response Latency',
          description: `${Math.round(callStats.avg_model_latency)}ms average response time`,
          recommendation: 'Consider optimizing model configuration for faster responses',
          icon: '🐌'
        });
      } else if (callStats.avg_model_latency < 1000) {
        insights.push({
          type: 'success',
          category: 'performance',
          title: 'Excellent Response Speed',
          description: `${Math.round(callStats.avg_model_latency)}ms average response time`,
          recommendation: 'Maintain current configuration for optimal performance',
          icon: '⚡'
        });
      }
    }

    // Memory Performance Analysis
    if (memoryStats.total_memories > 0) {
      // Memory Creation Rate
      const weeklyRate = memoryStats.avg_memories_per_week;
      if (weeklyRate >= 2) {
        insights.push({
          type: 'success',
          category: 'memories',
          title: 'Active Memory Creation',
          description: `Creating ${weeklyRate} memories per week on average`,
          recommendation: 'Excellent engagement with memory system',
          icon: '🧠'
        });
      } else if (weeklyRate < 0.5) {
        insights.push({
          type: 'info',
          category: 'memories',
          title: 'Low Memory Activity',
          description: `Only ${weeklyRate} memories per week on average`,
          recommendation: 'Encourage more frequent memory documentation',
          icon: '📝'
        });
      }

      // Category Diversity
      const categoryUsage = memoryStats.categories_used / 7;
      if (categoryUsage >= 0.8) {
        insights.push({
          type: 'success',
          category: 'memories',
          title: 'Diverse Memory Categories',
          description: `Using ${memoryStats.categories_used} of 7 memory categories`,
          recommendation: 'Great variety in memory types captured',
          icon: '🌈'
        });
      } else if (categoryUsage < 0.4) {
        insights.push({
          type: 'info',
          category: 'memories',
          title: 'Limited Category Usage',
          description: `Only using ${memoryStats.categories_used} of 7 memory categories`,
          recommendation: 'Explore additional memory categories for richer documentation',
          icon: '📊'
        });
      }
    }

    // Engagement Analysis
    const recentActivity = (callStats.calls_this_week || 0) + (memoryStats.memories_this_week || 0);
    if (recentActivity >= 5) {
      insights.push({
        type: 'success',
        category: 'engagement',
        title: 'High Weekly Engagement',
        description: `${recentActivity} activities this week`,
        recommendation: 'Maintain this excellent level of system engagement',
        icon: '🔥'
      });
    } else if (recentActivity === 0) {
      insights.push({
        type: 'warning',
        category: 'engagement',
        title: 'No Recent Activity',
        description: 'No contacts or memories created this week',
        recommendation: 'Check in with user to understand any barriers to engagement',
        icon: '😴'
      });
    }

    return insights;
  }, [callStats, memoryStats]);

  // Performance metrics for charts
  const performanceMetrics = useMemo(() => {
    if (!callStats) return [];

    const allMetrics = [
      {
        name: 'Success Rate',
        value: callStats.success_rate || 0,
        target: 80,
        unit: '%',
        color: callStats.success_rate >= 80 ? 'green' : callStats.success_rate >= 60 ? 'yellow' : 'red'
      },
      {
        name: 'Avg Duration',
        value: callStats.avg_duration_minutes || 0,
        target: 10,
        unit: 'min',
        color: 'blue'
      },
      {
        name: 'Response Time',
        value: callStats.avg_model_latency || 0,
        target: 1000,
        unit: 'ms',
        color: callStats.avg_model_latency <= 1000 ? 'green' : callStats.avg_model_latency <= 2000 ? 'yellow' : 'red',
        inverted: true // Lower is better
      },
      {
        name: 'Weekly Contacts',
        value: callStats.calls_this_week || 0,
        target: 3,
        unit: 'contacts',
        color: 'purple'
      }
    ];

    // Filter out Response Time card for Coach users
    return userRole === 'COACH'
      ? allMetrics.filter(metric => metric.name !== 'Response Time')
      : allMetrics;
  }, [callStats, userRole]);

  // Memory breakdown data
  const memoryBreakdown = useMemo(() => {
    if (!memoryStats?.category_breakdown) return [];

    return Object.entries(memoryStats.category_breakdown).map(([category, count]) => {
      const categoryInfo = getCategoryInfo(category);
      return {
        category,
        count,
        percentage: Math.round((count / memoryStats.total_memories) * 100),
        ...categoryInfo
      };
    }).sort((a, b) => b.count - a.count);
  }, [memoryStats]);

  // Get insight color classes
  const getInsightClasses = (type) => {
    switch (type) {
      case 'success':
        return 'border-green-200 bg-green-50 text-green-800';
      case 'warning':
        return 'border-yellow-200 bg-yellow-50 text-yellow-800';
      case 'error':
        return 'border-red-200 bg-red-50 text-red-800';
      default:
        return 'border-blue-200 bg-blue-50 text-blue-800';
    }
  };

  // Progress bar component
  const ProgressBar = ({ value, target, color, inverted = false }) => {
    const percentage = inverted 
      ? Math.max(0, Math.min(100, ((target - value) / target) * 100))
      : Math.min(100, (value / target) * 100);

    const getColorClass = () => {
      switch (color) {
        case 'green': return 'bg-green-500';
        case 'yellow': return 'bg-yellow-500';
        case 'red': return 'bg-red-500';
        case 'blue': return 'bg-blue-500';
        case 'purple': return 'bg-purple-500';
        default: return 'bg-gray-500';
      }
    };

    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div 
          className={`h-2 rounded-full transition-all duration-500 ${getColorClass()}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    );
  };

  if (!callStats && !memoryStats) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-400 text-4xl mb-4">📊</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Performance Data</h3>
        <p className="text-gray-600">Performance analytics will appear once the user has some activity.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Performance Analytics</h3>
          <p className="text-sm text-gray-600">
            Comprehensive performance insights and recommendations
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          
          <button
            onClick={onRefresh}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Key Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {performanceMetrics.map((metric) => (
          <div key={metric.name} className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">{metric.name}</h4>
              <span className={`text-xs px-2 py-1 rounded-full ${
                metric.color === 'green' ? 'bg-green-100 text-green-800' :
                metric.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                metric.color === 'red' ? 'bg-red-100 text-red-800' :
                metric.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                Target: {metric.target} {metric.unit}
              </span>
            </div>
            
            <div className="text-2xl font-bold text-gray-900 mb-2">
              {metric.value.toFixed(metric.unit === 'ms' ? 0 : 1)} {metric.unit}
            </div>
            
            <ProgressBar 
              value={metric.value} 
              target={metric.target} 
              color={metric.color}
              inverted={metric.inverted}
            />
            
            <div className="text-xs text-gray-500 mt-1">
              {metric.inverted 
                ? `${Math.round(((metric.target - metric.value) / metric.target) * 100)}% of target`
                : `${Math.round((metric.value / metric.target) * 100)}% of target`
              }
            </div>
          </div>
        ))}
      </div>

      {/* Performance Insights */}
      {performanceInsights && performanceInsights.length > 0 && (
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-900">Performance Insights</h4>
            <p className="text-sm text-gray-600">AI-generated recommendations based on user activity</p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {performanceInsights.map((insight, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border-l-4 ${getInsightClasses(insight.type)}`}
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">{insight.icon}</span>
                    <div className="flex-1">
                      <h5 className="font-medium text-sm mb-1">{insight.title}</h5>
                      <p className="text-sm mb-2 opacity-90">{insight.description}</p>
                      <p className="text-xs font-medium opacity-75">{insight.recommendation}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Memory Category Breakdown - Hidden for Coach users */}
      {userRole !== 'COACH' && memoryBreakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-900">Memory Category Distribution</h4>
            <p className="text-sm text-gray-600">Breakdown of memory types and usage patterns</p>
          </div>
          
          <div className="p-6">
            <div className="space-y-4">
              {memoryBreakdown.map((category) => (
                <div key={category.category} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{category.icon}</span>
                    <span className="text-sm font-medium text-gray-900">{category.label}</span>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">{category.count} memories</div>
                      <div className="text-xs text-gray-500">{category.percentage}% of total</div>
                    </div>
                    
                    <div className="w-24">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-indigo-500 transition-all duration-500"
                          style={{ width: `${category.percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cost Analysis - Hidden for Coach users */}
      {userRole !== 'COACH' && callStats?.total_cost > 0 && (
        <div className="bg-white rounded-lg shadow border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-900">Cost Analysis</h4>
            <p className="text-sm text-gray-600">Usage costs and efficiency metrics</p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{formatCost(callStats.total_cost)}</div>
                <div className="text-sm text-gray-600">Total Cost</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{formatCost(callStats.avg_cost)}</div>
                <div className="text-sm text-gray-600">Average per Contact</div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">
                  {callStats.total_duration_minutes > 0
                    ? formatCost(callStats.total_cost / callStats.total_duration_minutes)
                    : '$0.00'
                  }
                </div>
                <div className="text-sm text-gray-600">Cost per Minute</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Performance Summary - Hidden for Coach users */}
      {userRole !== 'COACH' && (
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-6 border border-indigo-200">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Performance Summary</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">Strengths</h5>
              <ul className="space-y-1 text-sm text-gray-600">
                {callStats?.success_rate >= 70 && <li>• High contact success rate</li>}
                {callStats?.avg_model_latency <= 1500 && <li>• Fast response times</li>}
                {memoryStats?.avg_memories_per_week >= 1 && <li>• Regular memory creation</li>}
                {memoryStats?.categories_used >= 4 && <li>• Diverse memory categories</li>}
                {(callStats?.calls_this_week || 0) + (memoryStats?.memories_this_week || 0) >= 3 && <li>• Active weekly engagement</li>}
              </ul>
            </div>
            
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">Areas for Improvement</h5>
              <ul className="space-y-1 text-sm text-gray-600">
                {callStats?.success_rate < 70 && <li>• Increase contact success rate</li>}
                {callStats?.avg_model_latency > 2000 && <li>• Reduce response latency</li>}
                {memoryStats?.avg_memories_per_week < 1 && <li>• Create memories more frequently</li>}
                {memoryStats?.categories_used < 4 && <li>• Explore more memory categories</li>}
                {(callStats?.calls_this_week || 0) + (memoryStats?.memories_this_week || 0) < 2 && <li>• Increase weekly activity</li>}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceTab;